import { Inline, Link, Stack, Text } from "@softmax/design-system";
import { colors, spacing } from "@softmax/design-system/tokens.stylex";
import * as stylex from "@stylexjs/stylex";
import { PolicyStateFeedback, type PolicyResourceState } from "../policy-state";

export type PolicyRecentEpisodeRecord = {
  readonly id: string;
  readonly label: string;
  readonly status: string;
  readonly createdAt: string;
  readonly href: string;
  readonly policies?: number;
  readonly replayAvailable?: boolean;
  readonly replayHref?: string | undefined;
};
export type PolicyRecentEpisodesState = PolicyResourceState<{
  readonly rows: readonly PolicyRecentEpisodeRecord[];
  readonly emptyMessage?: string;
}>;
export type PolicyRecentEpisodesProps = {
  readonly state: PolicyRecentEpisodesState;
  readonly onRetry?: (() => void) | undefined;
  readonly "aria-label"?: string;
};
const styles = stylex.create({
  list: { listStyle: "none", margin: 0, padding: 0 },
  row: {
    borderTopWidth: { default: 1, ":first-child": 0 },
    borderTopStyle: "solid",
    borderTopColor: colors.border,
    paddingBlock: spacing.space12,
    minWidth: 0,
    overflowWrap: "anywhere",
  },
});

/** The application supplies canonical request and replay links for independently loaded recent episodes. */
export function PolicyRecentEpisodes({
  state,
  onRetry,
  "aria-label": label = "Recent episodes",
}: PolicyRecentEpisodesProps) {
  return (
    <Stack width="auto">
      <PolicyStateFeedback
        state={state}
        subject="recent episodes"
        onRetry={onRetry}
      />
      {state.status === "ready" &&
        (state.rows.length === 0 ? (
          <Text tone="muted">
            {state.emptyMessage ?? "No recent episodes for this version."}
          </Text>
        ) : (
          <ul aria-label={label} {...stylex.props(styles.list)}>
            {state.rows.map((row) => (
              <li key={row.id} {...stylex.props(styles.row)}>
                <Stack width="auto" gap={4}>
                  <Inline justify="between">
                    <Link focus="native" href={row.href}>
                      {row.label}
                    </Link>
                    {row.replayHref && (
                      <Link focus="native" href={row.replayHref}>
                        Watch replay
                      </Link>
                    )}
                  </Inline>
                  <Text size="small" tone="muted">
                    {row.status} · {row.createdAt}
                    {row.policies !== undefined &&
                      ` · ${row.policies} policies`}
                    {row.replayAvailable &&
                      !row.replayHref &&
                      " · Replay available"}
                  </Text>
                </Stack>
              </li>
            ))}
          </ul>
        ))}
    </Stack>
  );
}
